import { IconButton, Layout, LuIcon } from "@overmap-ai/blocks"
import { type Asset, type FormSubmission, type Issue, type Stored } from "@overmap-ai/common"
import { selectAssetsOfProject, selectFormSubmissionsOfProject, selectIssuesOfProject } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ClearMapFilterButton } from "../../components/Buttons/ClearMapFilterButton"
import { MapFilterButton } from "../../components/Buttons/MapFilterButton"
import { ProjectSearchCommandMenu } from "../../components/CommandMenus/ProjectSearchCommandMenu"
import { useAssetsWithIdentifiers } from "../../hooks/assets"
import { useFormSubmissionsWithIdentifiers } from "../../hooks/forms"
import { useIssuesWithIdentifiers } from "../../hooks/issues"
import { SLIDE_OUT_IDS } from "../../layouts"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"
import { BaseTopBar } from "../TopBars/BaseTopBar"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId")

export const MapViewTopBar = memo(() => {
	const { projectId } = routeApi.useParams()

	const navigate = useNavigate()
	const issues = useAppSelector(selectIssuesOfProject(projectId))
	const formSubmissions = useAppSelector(selectFormSubmissionsOfProject(projectId))
	const assets = useAppSelector(selectAssetsOfProject(projectId))

	const assetsWithIdentifiers = useAssetsWithIdentifiers(assets)
	const issuesWithIdentifiers = useIssuesWithIdentifiers(issues)
	const formSubmissionsWithIdentifiers = useFormSubmissionsWithIdentifiers(formSubmissions)

	const handleSelectIssue = useCallback(
		(issue: Stored<Issue>) => {
			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId/issues/$issueId",
				params: (prev) => ({ ...prev, issueId: issue.uuid }),
			})
		},
		[navigate],
	)
	const handleSelectAsset = useCallback(
		(asset: Stored<Asset>) => {
			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId/assets/$assetId",
				params: (prev) => ({ ...prev, assetId: asset.uuid }),
			})
		},
		[navigate],
	)

	const handleSelectFormSubmission = useCallback(
		(formSubmission: Stored<FormSubmission>) => {
			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId/forms/submissions/$formSubmissionId",
				params: (prev) => ({ ...prev, formSubmissionId: formSubmission.uuid }),
			})
		},
		[navigate],
	)

	return (
		<BaseTopBar
			leftContent={
				<>
					<ProjectSearchCommandMenu
						assetAndIdentifiers={assetsWithIdentifiers}
						issuesAndIdentifiers={issuesWithIdentifiers}
						formSubmissionsAndIdentifiers={formSubmissionsWithIdentifiers}
						onSelectIssue={handleSelectIssue}
						onSelectAsset={handleSelectAsset}
						onSelectFormSubmission={handleSelectFormSubmission}
					>
						<IconButton accentColor="base" variant="soft">
							<LuIcon icon={modelIconMapping.search} />
						</IconButton>
					</ProjectSearchCommandMenu>
					<Layout.SlideOutTrigger slideOutId={SLIDE_OUT_IDS.RIGHT}>
						<MapFilterButton />
					</Layout.SlideOutTrigger>
					<ClearMapFilterButton variant="fill" />
				</>
			}
			showRightSlideOutButton={true}
		/>
	)
})
MapViewTopBar.displayName = "MapViewTopBar"
